import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  AppBar,
  Toolbar,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ActivityCard from "./ActivityCard";
import Frames from "./Frames";
import UserAuthButton from "../auth/UserAuthButton";
import AuthModal from "../auth/AuthModal";
import { useAuthContext } from "../../../context/AuthContext";
import FullscreenContentModal from "./FullscreenContentModal";
import BingoFlashIframe from "./BingoFlashIframe";
import ForceInviteSplash from "./ForceInviteSplash";
import {
  clearStaleGameNav,
  closeGame,
  ensureGameOpen,
  getGameNav,
  goTo as goToGameScreen,
  subscribeGameNav,
} from "../../../utils/mymGameHistory";
// import BingoTopLeaderboard from './BingoTopLeaderboard';

const getMymModalSpec = (gameId) => {
  if (typeof window === "undefined" || !gameId) return null;
  const origin = window.location.origin;
  const specs = {
    jukebox: { src: "https://songq.live/?jukebox=true", title: "Live Music Jukebox" },
    openmic: { src: "https://songq.live/?openmic=true", title: "Open Mic" },
    bingo: { src: "https://rockstar.bingo/join/goancrazy", title: "Live Music Bingo" },
    buzzer: { src: `${origin}/buzzer`, title: "Beat the Buzzer" },
    flash_bingo: { src: "INTERNAL_BINGO", title: "Flash Bingo" },
    insta_quiz: { src: `${origin}/quiz?quizMode=flash`, title: "Insta Quiz" },
    higher_or_lower: { src: `${origin}/higherorlower`, title: "Higher or Lower" },
    spinning_wheel: { src: `${origin}/spinningwheel`, title: "Spin the Wheel" },
    beerillioner: { src: `${origin}/beerillioner`, title: "Who Wants to Beer Millionaire?" },
  };
  return specs[gameId] || null;
};

const MymMaster = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSrc, setModalSrc] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [forceFullscreen, setForceFullscreen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [bingoStage, setBingoStage] = useState("intro"); // 'intro' | 'play'
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [bingoUrl, setBingoUrl] = useState(
    "https://rockstar.bingo/join/goancrazy",
  );
  const [pendingBingoContinue, setPendingBingoContinue] = useState(false);
  const [pendingAuthGame, setPendingAuthGame] = useState(null); // 'flash_bingo' | 'higher_or_lower' | 'higher_or_lower_force' | 'spinning_wheel' | 'spinning_wheel_force'
  const [mymConfig, setMymConfig] = useState([]);
  const [splashOpen, setSplashOpen] = useState(false);
  const [splashTitle, setSplashTitle] = useState("");
  const [splashDescription, setSplashDescription] = useState("");
  const [splashProceed, setSplashProceed] = useState(true);
  const [forceInviteOpen, setForceInviteOpen] = useState(false);
  const [forceInviteGameName, setForceInviteGameName] = useState("");
  const [forceInviteConfig, setForceInviteConfig] = useState({
    title: "You're Invited to Join the Game!",
    message:
      "The host has picked something special just for you. Get ready — the fun is about to begin!",
    timerSeconds: 15,
  });
  const splashOnProceedRef = useRef(null);
  const openedForceIdRef = useRef(null);
  const modalGameIdRef = useRef("");
  const lastModalByGameRef = useRef({});
  const forceFullscreenRef = useRef(false);
  const { isAuthenticated, user, isLoading } = useAuthContext();
  const authJustCompletedRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchMymConfig = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/settings/public/mym-titles-config`
        );
        const data = await res.json();
        if (data.config && Array.isArray(data.config)) {
          setMymConfig(data.config);
        }
        if (data.forceInvite) {
          setForceInviteConfig((prev) => ({
            ...prev,
            ...data.forceInvite,
          }));
        }
      } catch (err) {
        console.error("Failed to fetch MYM config:", err);
      }
    };
    fetchMymConfig();
  }, []);

  // When redirected from game pages with openAuth (e.g. /higherorlower, /spinningwheel)
  // Show auth modal on MYM home page instead of inside the game
  useEffect(() => {
    const state = location.state;
    const params = new URLSearchParams(location.search || "");
    const openAuth = state?.openAuth || params.get("openAuth") === "1";
    const returnTo = state?.returnTo || params.get("returnTo");
    if (openAuth && returnTo && !isLoading) {
      setPendingAuthGame(returnTo);
      setAuthOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.search, location.pathname, isLoading, navigate]);

  // When navigated with ?forceId=<tileId>, show force-invite splash then open game (Force mode)
  // Keep forceId in URL so MymForceNavigator can enforce routing and back-button prevention
  useEffect(() => {
    const search = location.search || "";
    const params = new URLSearchParams(search);
    const forceId = params.get("forceId");
    if (!forceId) return;

    // Only open once per forceId to prevent nested modals
    if (openedForceIdRef.current === forceId) return;
    openedForceIdRef.current = forceId;

    // Skip splash/proceed — show force-invite splash then open game (forceId in URL means Force mode)
    openSplashThenMaybeProceed(forceId, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname]);

  const openModal = (src, title, forceLocked = false, gameId = "") => {
    setForceFullscreen(!!forceLocked);
    forceFullscreenRef.current = !!forceLocked;
    setModalSrc(src);
    setModalTitle(title);
    setModalOpen(true);
    modalGameIdRef.current = gameId || "";
    if (gameId) lastModalByGameRef.current[gameId] = { src, title };
  };

  const closeModalUi = useCallback(() => {
    setExitConfirmOpen(false);
    setModalOpen(false);
    setBingoStage("intro");
    setForceFullscreen(false);
    forceFullscreenRef.current = false;
  }, []);

  const requestCloseModal = useCallback(() => {
    const nav = getGameNav();
    if (nav?.forceLocked) return;
    if (nav) {
      closeGame();
      return;
    }
    closeModalUi();
  }, [closeModalUi]);

  const modalOpenRef = useRef(false);
  modalOpenRef.current = modalOpen;
  forceFullscreenRef.current = forceFullscreen;

  useEffect(() => {
    clearStaleGameNav();
  }, []);

  useEffect(() => {
    const unsub = subscribeGameNav((nav) => {
      if (!nav) {
        if (modalOpenRef.current) closeModalUi();
        return;
      }
      const gameChanged = modalGameIdRef.current !== nav.game || !modalOpenRef.current;
      modalGameIdRef.current = nav.game || "";
      setForceFullscreen(!!nav.forceLocked);
      forceFullscreenRef.current = !!nav.forceLocked;
      setModalOpen(true);

      if (nav.game === "flash_bingo") {
        setModalSrc("INTERNAL_BINGO");
        setModalTitle("Flash Bingo");
        setBingoStage(nav.screen === "game" || nav.screen === "play" ? "play" : "intro");
        return;
      }

      if (!gameChanged) return;

      const remembered = lastModalByGameRef.current[nav.game];
      const spec = getMymModalSpec(nav.game);
      const nextSrc = remembered?.src || spec?.src;
      const nextTitle = remembered?.title || spec?.title || "";
      if (nextSrc) setModalSrc(nextSrc);
      if (nextTitle) setModalTitle(nextTitle);
    });
    return unsub;
  }, [closeModalUi]);

  useEffect(() => {
    const onLeave = (event) => {
      if (!modalOpenRef.current) return;
      event.preventDefault();
      closeModalUi();
    };
    window.addEventListener("browser-back-leave", onLeave);
    return () => window.removeEventListener("browser-back-leave", onLeave);
  }, [closeModalUi]);

  const getConfigForId = (id) => {
    const raw = mymConfig.find((c) => c.id === id);
    if (!raw)
      return {
        hidden: false,
        enabled: true,
        splashTitle: "",
        splashDescription: "",
        forceModal: false,
      };
    return {
      hidden: !!raw.hidden,
      enabled: !!raw.enabled,
      splashTitle: raw.splashTitle ?? "",
      splashDescription: raw.splashDescription ?? "",
      forceModal: !!raw.forceModal,
    };
  };

  const handleJukeboxClick = (forceLocked = false) => {
    openModal(
      "https://songq.live/?jukebox=true",
      "Live Music Jukebox",
      forceLocked,
      "jukebox",
    );
  };

  const handleOpenMicClick = (forceLocked = false) => {
    openModal("https://songq.live/?openmic=true", "Open Mic", forceLocked, "openmic");
  };

  const handleBingoClick = (forceLocked = false) => {
    openModal(
      "https://rockstar.bingo/join/goancrazy",
      "Live Music Bingo",
      forceLocked,
      "bingo",
    );
  };

  const handleContestantClick = (forceLocked = false) => {
    const baseUrl = window.location.origin;
    openModal(`${baseUrl}/buzzer`, "Beat the Buzzer", forceLocked, "buzzer");
  };


  const handleFlashBingoClick = (forceLocked = false) => {
    if (!isLoading && (!isAuthenticated || !user)) {
      setPendingAuthGame("flash_bingo");
      setAuthOpen(true);
      return;
    }
    setModalSrc("INTERNAL_BINGO");
    setBingoStage("intro");
    setModalTitle("Flash Bingo");
    setForceFullscreen(!!forceLocked);
    forceFullscreenRef.current = !!forceLocked;
    setBingoUrl("https://rockstar.bingo/join/bankai"); // Reset to default
    setModalOpen(true);
    modalGameIdRef.current = "flash_bingo";
    lastModalByGameRef.current.flash_bingo = { src: "INTERNAL_BINGO", title: "Flash Bingo" };
    if (!forceLocked) {
      ensureGameOpen({ game: "flash_bingo", screen: "home" });
    }
  };

  const handleFlashQuizClick = (forceLocked = false) => {
    const baseUrl = window.location.origin;
    const quizUrl = `${baseUrl}/quiz?quizMode=flash${forceLocked ? '&forceJoin=1' : ''}`;
    openModal(quizUrl, "Insta Quiz", forceLocked, "insta_quiz");
  };

  const runSplashProceed = useCallback(() => {
    if (splashOnProceedRef.current) splashOnProceedRef.current();
    setSplashOpen(false);
    setForceInviteOpen(false);
  }, []);

  const openSplashThenMaybeProceed = (id, isForcedFromUrl = false) => {
    const cfg = getConfigForId(id);
    const tile = MYM_TILES.find((t) => t.id === id);
    if (!tile) return;

    const forceLocked = !!cfg.forceModal || isForcedFromUrl;

    if (forceLocked) {
      setForceInviteGameName(cfg.splashTitle || tile.title || "");
      splashOnProceedRef.current = () => tile.onProceed(true);
      setForceInviteOpen(true);
      return;
    }

    const title = cfg.splashTitle || "";
    const desc = cfg.splashDescription || "";
    const proceed = cfg.enabled;
    setSplashTitle(title);
    setSplashDescription(desc);
    setSplashProceed(proceed);
    splashOnProceedRef.current = proceed
      ? () => {
          tile.onProceed(false);
        }
      : null;
    setSplashOpen(true);
  };

  // When admin turns Force OFF, close any forced modal/splash on this page
  useEffect(() => {
    const handler = () => {
      openedForceIdRef.current = null;
      if (forceFullscreen || splashOpen || forceInviteOpen) {
        setSplashOpen(false);
        setForceInviteOpen(false);
        closeModalUi();
      }
    };

    window.addEventListener("mym-force-clear", handler);
    return () => {
      window.removeEventListener("mym-force-clear", handler);
    };
  }, [forceFullscreen, splashOpen, forceInviteOpen, closeModalUi]);

  const handleBingoContinueInternal = useCallback(async () => {
    if (!isAuthenticated || !user) {
      // If still not authenticated, set pending flag
      setPendingBingoContinue(true);
      setAuthOpen(true);
      return;
    }

    // Just proceed to play stage with default URL
    setBingoUrl("https://rockstar.bingo/join/goancrazy");
    setBingoStage("play");
    if (!forceFullscreenRef.current) {
      goToGameScreen({ game: "flash_bingo", screen: "game" });
    }
  }, [isAuthenticated, user]);

  const handleBingoContinue = async () => {
    // If auth was just completed, skip the check
    if (authJustCompletedRef.current) {
      authJustCompletedRef.current = false;
    } else if (!isAuthenticated || !user) {
      setAuthOpen(true);
      return;
    }

    await handleBingoContinueInternal();
  };

  // Auto-trigger Continue when auth completes
  useEffect(() => {
    if (
      pendingBingoContinue &&
      isAuthenticated &&
      user &&
      modalSrc === "INTERNAL_BINGO" &&
      bingoStage === "intro"
    ) {
      setPendingBingoContinue(false);
      // Small delay to ensure auth state is fully updated
      setTimeout(() => {
        handleBingoContinueInternal();
      }, 300);
    }
  }, [
    isAuthenticated,
    user,
    pendingBingoContinue,
    modalSrc,
    bingoStage,
    handleBingoContinueInternal,
  ]);

  const handleHigherOrLowerClick = (forceLocked = false) => {
    if (!isLoading && (!isAuthenticated || !user)) {
      setPendingAuthGame(forceLocked ? "higher_or_lower_force" : "higher_or_lower");
      setAuthOpen(true);
      return;
    }
    const baseUrl = window.location.origin;
    const query = forceLocked ? "?mode=sp&forceJoin=1" : "";
    openModal(`${baseUrl}/higherorlower${query}`, "Higher or Lower", forceLocked, "higher_or_lower");
  };

  const handleSpinningWheelClick = (forceLocked = false) => {
    if (!isLoading && (!isAuthenticated || !user)) {
      setPendingAuthGame(forceLocked ? "spinning_wheel_force" : "spinning_wheel");
      setAuthOpen(true);
      return;
    }
    const baseUrl = window.location.origin;
    const query = forceLocked ? "?mode=host&forceJoin=1" : "";
    openModal(`${baseUrl}/spinningwheel${query}`, "Spin the Wheel", forceLocked, "spinning_wheel");
  };

  const handleBeerillionerClick = (forceLocked = false) => {
    const baseUrl = window.location.origin;
    openModal(
        `${baseUrl}/beerillioner`,
        "Who Wants to Beer Millionaire?",
      forceLocked,
      "beerillioner",
    );
  };

  const MYM_TILES = [
    {
      id: "jukebox",
      image: "./jukebox_logo.png",
      alt: "Jukebox",
      title: "LIVE MUSIC JUKE BOX",
      description:
        "Make the band play your way. Request the songs you love, hear them played live, and own the soundtrack to your night.",
      buttonText: "Play Juke Box",
      onProceed: handleJukeboxClick,
    },
    {
      id: "openmic",
      image: "./openmic_logo.png",
      alt: "Open Mic",
      title: "OPEN MIC",
      description:
        "Pick It. Sing It. Own It. Choose the songs you love with the Live Music Jukebox, then take the mic yourself during Open Mic Hour.",
      buttonText: "Rock The Mic",
      onProceed: handleOpenMicClick,
    },
    {
      id: "bingo",
      image: "./bingoLogo.png",
      alt: "Bingo",
      title: "LIVE MUSIC BINGO",
      description:
        "Hear It. Mark It. Win It. Dive into fast-paced, high-energy fun rounds of Live Music Bingo where every song could score you a win.",
      buttonText: "Play Win Big",
      onProceed: handleBingoClick,
    },
    {
      id: "buzzer",
      image: "./beatTheBuzzer2.png",
      alt: "Beat the Buzzer",
      title: "BEAT THE BUZZER",
      description:
        "Sign up for crowd-favourite games—Complete the Song Lyrics, Guess the Song Name, and Music Charades. Win over the audience and walk away with prizes.",
      buttonText: "Take Centre Stage",
      onProceed: handleContestantClick,
    },
    {
      id: "flash_bingo",
      image: "./FlashBingo2.png",
      alt: "Flash Bingo",
      title: "FLASH BINGO",
      description:
        "Lightning-fast rounds of music bingo! Mark songs as they play, answer quick quiz questions, and climb the leaderboard to win amazing prizes.",
      buttonText: "Play Flash Bingo",
      onProceed: handleFlashBingoClick,
    },
    {
      id: "insta_quiz",
      image: "./FlashQuiz2.png",
      alt: "Insta Quiz",
      title: "INSTA QUIZ",
      description:
        "Test your music knowledge with rapid-fire questions! Quick rounds, instant results, and exciting prizes await the fastest and smartest players.",
      buttonText: "Play Insta Quiz",
      onProceed: handleFlashQuizClick,
    },
    {
      id: "higher_or_lower",
      image: "/assets/higherorlowerLogo.png",
      alt: "Higher or Lower",
      title: "HIGHER OR LOWER",
      description:
        "Guess whether the next song is higher or lower on the charts. Test your music knowledge and climb the leaderboard.",
      buttonText: "Play Higher or Lower",
      onProceed: handleHigherOrLowerClick,
    },
    {
      id: "spinning_wheel",
      image: "/assets/spinthewheel.png",
      alt: "Spin the Wheel",
      title: "SPIN THE WHEEL",
      description:
        "Spin the wheel for a chance to win. Exciting prizes and non-stop fun await with every spin.",
      buttonText: "Spin the Wheel",
      onProceed: handleSpinningWheelClick,
    },
    {
      id: "beerillioner",
      image: "/beer2.png",
      alt: "Beerillioner",
      title: "WHO WANTS TO BEER MILLIONAIRE?",
      description:
        "Step into the hot seat and climb the prize ladder with every correct answer. Use lifelines wisely and try to become a true Beer Millionaire.",
      buttonText: "Play Beer Millionaire",
      onProceed: handleBeerillionerClick,
    },
  ];

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      setMessage("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    setMessage("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/newsletter/subscribe`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        },
      );

      const data = await response.json();

      if (data.success) {
        setMessage(
          "Successfully subscribed! Check your email for confirmation.",
        );
        setEmail("");
      } else {
        setMessage(data.message || "Failed to subscribe. Please try again.");
      }
    } catch (error) {
      setMessage("Failed to subscribe. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // const handleCloseIframe = () => {
  // setShowIframe(false);
  // setIframeSrc('');
  // setIframeTitle('');
  // };

  return (
    <Box
      minHeight="100vh"
      sx={{
        background:
          "linear-gradient(180deg, #0a0015 0%, #1a0033 50%, #0a0015 100%)",
        padding: "0",
        overflowX: "hidden",
      }}
    >
      {/* Enhanced Header with Auth */}
      <AppBar
        position="static"
        sx={{
          background:
            "linear-gradient(90deg, rgba(0,0,0,0.95) 0%, rgba(139,0,139,0.3) 50%, rgba(0,0,0,0.95) 100%)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 4px 30px rgba(255,0,255,0.3)",
          borderBottom: "2px solid",
          borderImage:
            "linear-gradient(90deg, transparent, #ff00ff, #00ffff, #ff00ff, transparent) 1",
        }}
      >
        <Toolbar
          sx={{
            maxWidth: "1400px",
            width: "100%",
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: { xs: 1, md: 1.5 },
            px: { xs: 2, md: 4 },
            minHeight: { xs: "40px", md: "50px" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              component="img"
              src="/mind_your_music.png"
              alt="Mind Your Music"
              sx={{
                height: { xs: "50px", md: "60px" },
                objectFit: "contain",
              }}
            />
          </Box>
          <UserAuthButton />
        </Toolbar>
      </AppBar>

      {/* 8 Image Boxes Section - Compact to fit in 100vh with header */}
      <Box
        sx={{
          width: "100%",
          padding: {
            xs: "0.75rem 0.75rem",
            sm: "1rem 1rem",
            md: "1.25rem 1.5rem",
          },
          background:
            "radial-gradient(circle at 50% 50%, rgba(255,0,255,0.15) 0%, transparent 50%), linear-gradient(180deg, #0a0015 0%, #1a0033 50%, #0a0015 100%)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 20% 80%, rgba(0,255,255,0.1) 0%, transparent 40%), radial-gradient(circle at 80% 20%, rgba(255,0,255,0.1) 0%, transparent 40%)",
            pointerEvents: "none",
          },
        }}
      >
        <Box
          sx={{
            maxWidth: "1400px",
            width: { md: "80%", xs: "100%" },
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: { xs: "0.5rem", sm: "0.75rem", md: "1rem" },
            padding: { xs: "0.5rem", sm: "0.75rem", md: "1rem" },
          }}
        >
          {MYM_TILES.filter((tile) => !getConfigForId(tile.id).hidden).map(
            (tile) => {
              const cfg = getConfigForId(tile.id);
              const isDisabled = !cfg.enabled && !cfg.forceModal;
              return (
                <Box
                  key={tile.id}
                  onClick={() => openSplashThenMaybeProceed(tile.id)}
                  sx={{
                  borderRadius: "16px",
                  overflow: "hidden",
                  background: isDisabled
                    ? "linear-gradient(135deg, rgba(30,30,40,0.9) 0%, rgba(40,40,50,0.9) 100%)"
                    : "linear-gradient(135deg, rgba(20,0,40,0.9) 0%, rgba(40,0,60,0.9) 100%)",
                  border: "2px solid transparent",
                  backgroundClip: "padding-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: { xs: "0.5rem", sm: "0.75rem", md: "1rem" },
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  height: { xs: "140px", sm: "170px", md: "200px" },
                  position: "relative",
                  boxShadow: isDisabled ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 20px rgba(255,0,255,0.2)",
                  opacity: isDisabled ? 0.7 : 1,
                  filter: isDisabled ? "grayscale(0.6)" : "none",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: "16px",
                    padding: "2px",
                    background: "linear-gradient(135deg, #ff00ff, #00ffff)",
                    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                    opacity: isDisabled ? 0.3 : 0.5,
                  },
                  "&:hover": {
                    transform: isDisabled ? "none" : "translateY(-4px) scale(1.01)",
                    boxShadow: isDisabled ? undefined : "0 12px 36px rgba(255,0,255,0.4), 0 0 20px rgba(0,255,255,0.2)",
                    "&::before": { opacity: isDisabled ? 0.3 : 1 },
                  },
                  }}
                >
                {cfg.enabled && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 2,
                      px: 1,
                      py: 0.25,
                      borderRadius: "6px",
                      background: "linear-gradient(135deg, #e53935 0%, #ff5722 100%)",
                      boxShadow: "0 2px 6px rgba(229,57,53,0.5)",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#fff", fontWeight: 700, fontSize: "0.65rem", letterSpacing: "0.5px" }}>
                      ON AIR
                    </Typography>
                  </Box>
                )}
                <Box
                  component="img"
                  src={tile.image}
                  alt={tile.alt}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Hero Section with Gradient Background */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: { xs: "90vh", md: "90vh" },
          background:
            "radial-gradient(circle at 50% 0%, rgba(138,43,226,0.4) 0%, transparent 50%), linear-gradient(180deg, #0a0015 0%, #1a0033 50%, #0a0015 100%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: { xs: "3rem 1rem", md: "5rem 2rem" },
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 30% 20%, rgba(255,0,255,0.15) 0%, transparent 40%), radial-gradient(circle at 70% 80%, rgba(0,255,255,0.15) 0%, transparent 40%)",
            pointerEvents: "none",
          },
        }}
      >
        {/* Hero Content */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            maxWidth: "1200px",
            width: "80%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: 3, md: 4 },
          }}
        >
          {/* Main Heading */}
          <Typography
            variant="h1"
            sx={{
              background:
                "linear-gradient(135deg, #ff00ff 0%, #00ffff 50%, #ff00ff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontWeight: 900,
              textAlign: "center",
              fontSize: {
                xs: "2.5rem",
                sm: "3.5rem",
                md: "4.5rem",
                lg: "5.5rem",
              },
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              mb: 2,
              pb: 3,
              filter: "drop-shadow(0 2px 8px rgba(255,0,255,0.3))",
            }}
          >
            Your Music.
            <br />
            Your Show.
            <br />
            Your Night.
          </Typography>

          {/* Subheading Question */}
          <Typography
            variant="h5"
            sx={{
              color: "#ffffff",
              textAlign: "center",
              fontSize: { xs: "1.2rem", md: "1.6rem" },
              fontWeight: 500,
              mb: { xs: 3, md: 5 },
              textShadow: "0 2px 8px rgba(0,0,0,0.5)",
              letterSpacing: "0.5px",
            }}
          >
            How will you make your night unforgettable?
          </Typography>

          {/* Activity Cards Grid - 8 titles (driven by MYM config) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: { xs: 1.5, md: 2 },
              width: "100%",
              maxWidth: "1200px",
              mt: { xs: 2, md: 4 },
            }}
          >
            {MYM_TILES.filter((tile) => !getConfigForId(tile.id).hidden).map(
              (tile) => {
                const cfg = getConfigForId(tile.id);
                return (
                  <ActivityCard
                    key={tile.id}
                    image={tile.image}
                    title={tile.title}
                    description={tile.description}
                    buttonText={tile.buttonText}
                    enabled={cfg.enabled}
                    showOnAirRibbon={cfg.enabled}
                    onClick={() => openSplashThenMaybeProceed(tile.id)}
                  />
                );
              },
            )}
          </Box>
        </Box>
      </Box>

      {/* Content Section */}
      <Box
        sx={{
          padding: { xs: "3rem 1rem", md: "5rem 2rem" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "3rem",
          width: "100%",
          background:
            "linear-gradient(180deg, #0a0015 0%, #1a0033 50%, #0a0015 100%)",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 50% 50%, rgba(255,0,255,0.05) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        {/* Welcome Section */}
        <Box
          sx={{
            maxWidth: "1200px",
            width: "100%",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              background: "linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontWeight: 800,
              marginBottom: "1.5rem",
              fontSize: { xs: "2rem", md: "3rem" },
              filter: "drop-shadow(0 2px 6px rgba(255,0,255,0.3))",
            }}
          >
            Welcome to Mind Your Music Live
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.9)",
              lineHeight: 1.8,
              fontSize: { xs: "1rem", md: "1.2rem" },
              maxWidth: "800px",
              margin: "0 auto",
              textShadow: "0 2px 10px rgba(0,0,0,0.5)",
            }}
          >
            Where you pick the music, join in music games, step up for Showtime
            challenges, and own Open Mic Hour while showing off your musical
            style. What more could you want? Join the fun. Make the night
            unforgettable.
          </Typography>
        </Box>

        {/* Come Join The Fun Section */}
        <Box
          sx={{
            maxWidth: "1200px",
            width: "100%",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              color: "#ffffff",
              fontWeight: 800,
              marginBottom: "1.5rem",
              fontSize: { xs: "2rem", md: "3rem" },
              textShadow: "0 2px 10px rgba(255,0,255,0.3)",
            }}
          >
            Come Join The Fun!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.9)",
              lineHeight: 1.8,
              fontSize: { xs: "1rem", md: "1.2rem" },
              maxWidth: "800px",
              margin: "0 auto",
              textShadow: "0 2px 10px rgba(0,0,0,0.5)",
            }}
          >
            Grab yourself a drink, you've just landed at Mind Your Music Live—an
            unforgettable night where live music, huge games, and electric crowd
            energy collide. Brace yourself for party anthems, bingo beats, quiz
            battles, and sing-your-heart-out finales.
          </Typography>
        </Box>

        {/* Quiz Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Button
            variant="contained"
            onClick={() => {
              window.location.href = "/quiz";
            }}
            sx={{
              background: "linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)",
              color: "white",
              padding: "1rem 3rem",
              fontSize: "1.1rem",
              fontWeight: 700,
              borderRadius: "50px",
              textTransform: "none",
              boxShadow: "0 4px 20px rgba(255,0,255,0.3)",
              position: "relative",
              overflow: "hidden",
              border: "2px solid rgba(255,255,255,0.2)",
              "&:hover": {
                background: "linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)",
                boxShadow: "0 6px 28px rgba(255,0,255,0.4)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Play Quiz
          </Button>
        </Box>

        {/* Fourth Content Block - About Us */}
        <Box
          sx={{
            padding: { xs: "1rem 0", md: "2rem 0" },
            maxWidth: "1200px",
            width: "100%",
            marginTop: "2rem",
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            sx={{
              color: "#ffffff",
              fontWeight: "bold",
              marginBottom: "1rem",
              fontSize: { xs: "1.5rem", md: "2rem" },
              textShadow: "0 2px 8px rgba(0,0,0,0.5)",
            }}
          >
            About Us
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.85)",
              lineHeight: 1.6,
              fontSize: { xs: "0.9rem", md: "1rem" },
              textAlign: "left",
              textShadow: "0 1px 5px rgba(0,0,0,0.5)",
            }}
          >
            Mind Your Music Live is an immersive, high-energy entertainment show
            that fuses live music with interactive games such as Live Music
            Bingo, Jukebox battles, Open Mic challenges, Music Charades — and
            win more. Put your name down to participate in your favourite games
            and join the action. Inspired by popular TV game shows, Mind Your
            Music Live brings a night like no other straight to your venue.
            Every show is packed with music, laughter, and unforgettable
            moments. To find out more, download our brochure here.
          </Typography>
        </Box>

        {/* News Section */}
        <Box
          sx={{
            padding: { xs: "1rem 0", md: "2rem 0" },
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
            gap: "1.5rem",
            marginTop: "2rem",
            width: "100%",
            maxWidth: "1200px",
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            sx={{
              color: "#ffffff",
              fontWeight: "bold",
              alignSelf: "flex-start",
              fontSize: { xs: "1.5rem", md: "2rem" },
              textShadow: "0 2px 8px rgba(0,0,0,0.5)",
            }}
          >
            News
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: "rgba(255,255,255,0.9)",
              textAlign: "left",
              fontSize: { xs: "1rem", md: "1.2rem" },
              textShadow: "0 1px 5px rgba(0,0,0,0.5)",
            }}
          >
            Get Our Latest jams, Games & Good times Straight to your inbox
          </Typography>
          <Box
            component="form"
            onSubmit={handleNewsletterSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: "1rem",
                flexDirection: { xs: "column", sm: "row" },
                width: "100%",
              }}
            >
              <TextField
                type="email"
                placeholder="Email"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{
                  backgroundColor: "rgba(255,255,255,0.05)",
                  borderRadius: "25px",
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255,255,255,0.05)",
                    borderRadius: "25px",
                    border: "2px solid rgba(255,0,255,0.3)",
                    "&:hover": {
                      borderColor: "rgba(255,0,255,0.6)",
                      boxShadow: "0 0 15px rgba(255,0,255,0.3)",
                    },
                    "&.Mui-focused": {
                      borderColor: "#ff00ff",
                      boxShadow: "0 0 20px rgba(255,0,255,0.5)",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    color: "#ffffff",
                    padding: ".75rem 1.5rem",
                    "&::placeholder": {
                      color: "rgba(255,255,255,0.5)",
                    },
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  background:
                    "linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)",
                  color: "white",
                  borderRadius: "25px",
                  padding: ".75rem 3rem",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  textTransform: "none",
                  width: { xs: "100%", sm: "auto" },
                  minWidth: "120px",
                  boxShadow: "0 4px 20px rgba(255,0,255,0.4)",
                  border: "2px solid rgba(255,255,255,0.2)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)",
                    boxShadow: "0 6px 30px rgba(255,0,255,0.6)",
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": {
                    background: "rgba(255,0,255,0.3)",
                    color: "rgba(255,255,255,0.5)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {isSubmitting ? "Sending..." : "Send"}
              </Button>
            </Box>
            {message && (
              <Typography
                sx={{
                  color: message.includes("Successfully")
                    ? "#4CAF50"
                    : "#ff6b6b",
                  fontSize: "0.9rem",
                  textAlign: "center",
                }}
              >
                {message}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1.5rem 2rem",
          borderTop: "2px solid",
          borderImage:
            "linear-gradient(90deg, transparent, #ff00ff, #00ffff, #ff00ff, transparent) 1",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: "1rem", sm: "0" },
          marginTop: "2rem",
          background: "rgba(0,0,0,0.5)",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "rgba(255,255,255,0.7)",
            fontSize: "0.8rem",
            width: "1200px",
            marginInline: "auto",
            textShadow: "0 1px 3px rgba(0,0,0,0.5)",
          }}
        >
          © 2025 mind your music. All rights reserved.
        </Typography>
      </Box>

      <FullscreenContentModal
        open={modalOpen}
        forceLocked={forceFullscreen}
        onClose={() => {
          if (modalSrc === "INTERNAL_BINGO" && bingoStage === "play") {
            setExitConfirmOpen(true);
          } else {
            requestCloseModal();
          }
        }}
      >
        {modalSrc === "INTERNAL_BINGO" ? (
          bingoStage === "intro" ? (
            <Box
              sx={{
                position: "relative",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                px: { xs: 2, md: 4 },
                background:
                  "radial-gradient(1200px 400px at 50% -10%, rgba(235,48,101,0.25), rgba(0,0,0,0))," +
                  "radial-gradient(800px 300px at 100% 100%, rgba(255,161,31,0.18), rgba(0,0,0,0))",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 1100,
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: "center",
                  gap: { xs: 3, md: 6 },
                }}
              >
                {/* Left: Text content */}
                <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" } }}>
                  <Box sx={{ display: "inline-flex", gap: 1, mb: 1 }}>
                    <Chip
                      label="Live Music"
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.08)",
                        color: "#fff",
                        border: "1px solid #333",
                      }}
                    />
                    <Chip
                      label="Bingo + Quiz"
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.08)",
                        color: "#fff",
                        border: "1px solid #333",
                      }}
                    />
                    <Chip
                      label="Prizes"
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.08)",
                        color: "#fff",
                        border: "1px solid #333",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      fontSize: { xs: "1.85rem", md: "2.5rem" },
                    }}
                  >
                    Welcome to Bingo Quiz
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mb: 2, color: "#bbb" }}>
                    Hear it. Mark it. Win it. Plus quick-fire quiz moments to
                    boost your score.
                  </Typography>
                  <Divider sx={{ my: 2, borderColor: "#222" }} />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.25,
                        alignItems: "flex-start",
                      }}
                    >
                      <CheckCircleOutlineIcon sx={{ color: "#ffa11f" }} />
                      <Typography variant="body2" sx={{ color: "#ddd" }}>
                        Join the round in seconds — no app needed
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.25,
                        alignItems: "flex-start",
                      }}
                    >
                      <CheckCircleOutlineIcon sx={{ color: "#ffa11f" }} />
                      <Typography variant="body2" sx={{ color: "#ddd" }}>
                        Mark songs as they play and rack up points
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.25,
                        alignItems: "flex-start",
                      }}
                    >
                      <CheckCircleOutlineIcon sx={{ color: "#ffa11f" }} />
                      <Typography variant="body2" sx={{ color: "#ddd" }}>
                        Answer surprise quiz questions for bonus
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.25,
                        alignItems: "flex-start",
                      }}
                    >
                      <CheckCircleOutlineIcon sx={{ color: "#ffa11f" }} />
                      <Typography variant="body2" sx={{ color: "#ddd" }}>
                        Climb the leaderboard and win prizes
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleBingoContinue}
                    startIcon={<PlayArrowIcon />}
                    sx={{
                      backgroundColor: "#eb3065",
                      color: "white",
                      borderRadius: "999px",
                      px: 5,
                      py: 1.25,
                      fontWeight: "bold",
                      textTransform: "none",
                      boxShadow: "0 8px 24px rgba(235,48,101,0.35)",
                      "&:hover": {
                        backgroundColor: "#c22553",
                        boxShadow: "0 8px 24px rgba(235,48,101,0.5)",
                      },
                    }}
                  >
                    Continue
                  </Button>
                </Box>

                {/* Right: Visual card */}
                <Box
                  sx={{
                    flex: 1,
                    width: "100%",
                    maxWidth: 520,
                    borderRadius: "16px",
                    overflow: "hidden",
                    border: "1px solid #222",
                    background:
                      "linear-gradient(135deg, rgba(255,161,31,0.12), rgba(235,48,101,0.12))",
                    boxShadow:
                      "0 20px 60px rgba(0,0,0,0.5) inset, 0 8px 28px rgba(0,0,0,0.4)",
                    p: { xs: 2.5, md: 3 },
                  }}
                >
                  <Box
                    component="img"
                    src="./bingoLogo.png"
                    alt="Bingo"
                    sx={{
                      display: "block",
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                      mb: 2,
                    }}
                  />
                  <Typography variant="body2" sx={{ color: "#ddd" }}>
                    You’ll join via rockstar.bingo in a secure in-app view. Keep
                    this window open during the round for best performance.
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <BingoFlashIframe url={bingoUrl} />
          )
        ) : (
          <Frames
            src={modalSrc}
            title={modalTitle}
            onLoad={() => {
              if (forceFullscreenRef.current) return;
              const id = modalGameIdRef.current;
              if (id) ensureGameOpen({ game: id, screen: "home" });
            }}
          />
        )}
      </FullscreenContentModal>

      <ForceInviteSplash
        open={forceInviteOpen}
        title={forceInviteConfig.title}
        message={forceInviteConfig.message}
        gameName={forceInviteGameName}
        timerSeconds={forceInviteConfig.timerSeconds}
        onComplete={runSplashProceed}
      />

      {/* Splash info modal: Proceed opens game, Close just dismisses */}
      <Dialog
        open={splashOpen}
        onClose={() => setSplashOpen(false)}
        PaperProps={{
          sx: {
            background: "linear-gradient(135deg, rgba(20,0,40,0.98) 0%, rgba(40,0,60,0.98) 100%)",
            backdropFilter: "blur(20px)",
            border: "2px solid rgba(255,0,255,0.4)",
            borderRadius: "16px",
            boxShadow: "0 20px 60px rgba(255,0,255,0.3)",
            minWidth: 320,
            maxWidth: 480,
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff", textAlign: "center", pt: 3, pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {splashTitle || "Info"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 2 }}>
          <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.9)", whiteSpace: "pre-wrap" }}>
            {splashDescription || ""}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3, px: 3 }}>
          <Button
            variant="contained"
            onClick={() => (splashProceed ? runSplashProceed() : setSplashOpen(false))}
            sx={{
              background: "linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)",
              color: "white",
              borderRadius: "25px",
              px: 4,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                background: "linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)",
                boxShadow: "0 6px 28px rgba(255,0,255,0.4)",
              },
            }}
          >
            {splashProceed ? "Proceed" : "Close"}
          </Button>
        </DialogActions>
      </Dialog>

      <AuthModal
        open={authOpen}
        onClose={() => {
          setAuthOpen(false);
          setPendingBingoContinue(false);
          setPendingAuthGame(null);
        }}
        onSuccess={(authenticatedUser) => {
          setAuthOpen(false);
          authJustCompletedRef.current = true;
          const baseUrl = window.location.origin;
          if (pendingAuthGame === "higher_or_lower") {
            setPendingAuthGame(null);
            openModal(`${baseUrl}/higherorlower`, "Higher or Lower", false, "higher_or_lower");
          } else if (pendingAuthGame === "higher_or_lower_force") {
            setPendingAuthGame(null);
            openModal(`${baseUrl}/higherorlower?mode=sp&forceJoin=1`, "Higher or Lower", true, "higher_or_lower");
          } else if (pendingAuthGame === "spinning_wheel") {
            setPendingAuthGame(null);
            openModal(`${baseUrl}/spinningwheel`, "Spin the Wheel", false, "spinning_wheel");
          } else if (pendingAuthGame === "spinning_wheel_force") {
            setPendingAuthGame(null);
            openModal(`${baseUrl}/spinningwheel?mode=host&forceJoin=1`, "Spin the Wheel", true, "spinning_wheel");
          } else if (pendingAuthGame === "flash_bingo" || modalSrc === "INTERNAL_BINGO" || pendingBingoContinue) {
            setPendingAuthGame(null);
            setModalSrc("INTERNAL_BINGO");
            setBingoStage("intro");
            setModalTitle("Flash Bingo");
            setModalOpen(true);
            modalGameIdRef.current = "flash_bingo";
            lastModalByGameRef.current.flash_bingo = { src: "INTERNAL_BINGO", title: "Flash Bingo" };
            if (!forceFullscreenRef.current) {
              ensureGameOpen({ game: "flash_bingo", screen: "home" });
            }
            setPendingBingoContinue(true);
          } else {
            setPendingAuthGame(null);
            setModalSrc("INTERNAL_BINGO");
            setBingoStage("intro");
            setModalTitle("Flash Bingo");
            setModalOpen(true);
            modalGameIdRef.current = "flash_bingo";
            lastModalByGameRef.current.flash_bingo = { src: "INTERNAL_BINGO", title: "Flash Bingo" };
            if (!forceFullscreenRef.current) {
              ensureGameOpen({ game: "flash_bingo", screen: "home" });
            }
          }
        }}
        mode="login"
      />

      {/* Exit Confirmation Dialog */}
      <Dialog
        open={exitConfirmOpen}
        onClose={() => setExitConfirmOpen(false)}
        PaperProps={{
          sx: {
            background:
              "linear-gradient(135deg, rgba(20,0,40,0.98) 0%, rgba(40,0,60,0.98) 100%)",
            backdropFilter: "blur(20px)",
            border: "2px solid rgba(255,0,255,0.4)",
            borderRadius: "16px",
            boxShadow:
              "0 20px 60px rgba(255,0,255,0.5), 0 0 40px rgba(0,255,255,0.3)",
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff", textAlign: "center", pt: 3, pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Are you really want to exit?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 2 }}>
          <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.8)" }}>
            Your progress will be saved, but you'll exit the Bingo Flash game.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3, px: 3 }}>
          <Button
            onClick={() => setExitConfirmOpen(false)}
            variant="outlined"
            sx={{
              color: "#fff",
              borderColor: "rgba(255,255,255,0.3)",
              borderRadius: "25px",
              px: 4,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                borderColor: "rgba(255,255,255,0.6)",
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setExitConfirmOpen(false);
              requestCloseModal();
            }}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)",
              color: "white",
              borderRadius: "25px",
              px: 4,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "0 4px 20px rgba(255,0,255,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)",
                boxShadow: "0 6px 28px rgba(255,0,255,0.4)",
              },
            }}
          >
            Exit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MymMaster;
